import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './main.css';
import useWindowDimensions from './hooks/useWindowDimensions';
import Puzzle from './Puzzle';
import Menu from './Menu';
import WinModal from './WinModal';
import { useTransition } from '@react-spring/web';
import Themes, { Content, PartialPuzzleData, PuzzleData, PuzzleSize, Theme } from './Themes';
import useSound from 'use-sound';
import buttonSfx from './sounds/take.mp3';

function App() {
	const windowDim = useWindowDimensions();
	const isApple = useMemo(() => /iPad|iPhone|iPod|Mac/.test(window.navigator.userAgent), []);
	const isFirstPlay = useRef(true);
	// const [, setIsFullscreen] = useState(false);
	// useEffect(() => {
	// 	function onFullscreenChange() {
	// 		setIsFullscreen(Boolean(document.fullscreenElement));
	// 	}
	// 	document.addEventListener('fullscreenchange', onFullscreenChange);
	// 	return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
	// }, []);
	const [isLoading, setIsLoading] = useState(true);
	const [theme, setTheme] = useState<Theme>(Themes.default.theme);
	const [menuTexts, setMenuTexts] = useState<string[]>(Themes.default.menuTexts);
	const [puzzles, setPuzzles] = useState<PartialPuzzleData[] | undefined>();
	const [defaultPuzzle, setDefaultPuzzle] = useState<PuzzleData | undefined>();

	const [cards, setCards] = useState<string[] | undefined>();
	const [selectedCard, setSelectedCard] = useState<string | undefined>();
	const numCards = 8;
	const remainingCards = useRef<string[]>([]);
	const [puzzle, setPuzzle] = useState<PuzzleData | undefined>();
	const [puzzleSize, setPuzzleSize] = useState<PuzzleSize | undefined>();

	const [isWinShow, setIsWinShow] = useState(false);
	const [playButtonSfx] = useSound(buttonSfx);

	useEffect(() => {
		const query = new URLSearchParams(window.location.search);
		const queryTheme = query.get('theme');
		const content: Content = queryTheme && Themes[queryTheme] ? Themes[queryTheme] : Themes.glavgeroi;
		setTheme(content.theme);
		setMenuTexts(content.menuTexts);
		setDefaultPuzzle(content.defaultPuzzle);
		setPuzzles(content.puzzles);

		const queryPuzzle = query.get('puzzle');
		if (queryPuzzle && content.puzzles.filter((p) => p.name === queryPuzzle).length > 0) {
			setMenuTexts([content.menuTexts[0], '«' + content.puzzles.filter((p) => p.name === queryPuzzle)[0].title + '»']);
			setCards([queryPuzzle]);
		} else {
			remainingCards.current = content.puzzles.map((puzzle) => puzzle.name).sort(() => Math.random() - 0.5);
			setCards(remainingCards.current.splice(0, numCards));
		}

		const querySize = query.get('size')?.split('x');
		if (querySize && querySize.length === 2) {
			const partial = content.puzzles.filter((p) => p.name === queryPuzzle).shift();
			if (partial !== undefined) {
				const querySizeXY = { x: parseInt(querySize[0]), y: parseInt(querySize[1]) };
				const size = partial.sizes
					? partial.sizes.filter((s) => s.x === querySizeXY.x && s.y === querySizeXY.y).shift()
					: content.defaultPuzzle.sizes.filter((s) => s.x === querySizeXY.x && s.y === querySizeXY.y).shift();
				if (size) {
					setPuzzle({ ...content.defaultPuzzle, ...partial });
					setPuzzleSize(size);
				}
			}
		}
		setIsLoading(false);
	}, []);

	const selectedSizes = useMemo(() => {
		const getPuzzleSizes = (name: string): number[] =>
			(puzzles?.filter((p) => p.name === name)[0]?.sizes ?? defaultPuzzle?.sizes)?.map((size) => size.x * size.y) ?? [];
		return selectedCard ? getPuzzleSizes(selectedCard) : [];
	}, [selectedCard, puzzles, defaultPuzzle]);

	const winModalTransition = useTransition(isWinShow, {
		from: { y: -100, opacity: 0 },
		enter: { y: 0, opacity: 1 },
		leave: { y: -100, opacity: 0 },
	});

	const onCardClick = useCallback((card: string) => {
		setSelectedCard((prev) => (prev ? undefined : card));
	}, []);

	const onSizeClick = useCallback(
		(size: number) => {
			if (puzzles) {
				const partial = puzzles.filter((p) => p.name === selectedCard).shift();
				if (partial && defaultPuzzle) {
					setPuzzle({ ...defaultPuzzle, ...partial });
					setPuzzleSize(partial.sizes?.[size] ?? defaultPuzzle.sizes[size]);
					if (!isApple && isFirstPlay.current) {
						isFirstPlay.current = false;
						document.body.requestFullscreen().then();
					}
				}
			}
		},
		[selectedCard, puzzles, defaultPuzzle, isApple, isFirstPlay]
	);

	const onPuzzleComplete = useCallback(() => {
		setIsWinShow(true);
	}, []);

	const onCardsGone = useCallback(() => {
		if (puzzles) {
			if (remainingCards.current.length < numCards) {
				remainingCards.current = puzzles.map((puzzle) => puzzle.name).sort(() => Math.random() - 0.5);
			}
			setCards(remainingCards.current.splice(0, numCards));
		}
	}, [remainingCards, puzzles]);

	const onCloseClick = useCallback(() => {
		setIsWinShow(false);
		onCardsGone()
		playButtonSfx();
	}, [playButtonSfx]);

	const onBackClick = useCallback(() => {
		// if (!isApple) {
		// 	document.exitFullscreen().then();
		// }
		setPuzzle(undefined);
		setSelectedCard(undefined);
		setIsWinShow(false);
		playButtonSfx();
	}, [playButtonSfx]);



	return (
		<div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: theme.background.color }}>
			{isLoading ? (
				<div />
			) : puzzle && puzzleSize ? (
				<Puzzle {...{ puzzle, onPuzzleComplete, onBackClick, theme, windowDim }} size={puzzleSize} />
			) : cards ? (
				<Menu {...{ menuTexts, cards, selectedCard, selectedSizes, onCardClick, onSizeClick, onCardsGone, theme, windowDim }} />
			) : (
				<p className="font-karusel text-3xl">Ошибка</p>
			)}
			{puzzle &&
				winModalTransition(
					(animatedStyle, iswinshow) =>
						iswinshow &&
						<WinModal
							onCloseClick={onCloseClick}
							title={puzzle.title}
							winTexts={puzzle.winTexts}
							{...{ animatedStyle, theme,
								windowDim
							}}
						/>
				)}
		</div>
	);
}

export default App;
